// Wave AW.3-React (2026-05-16) — 입찰 모달
// API: POST /buyer-app/auction/:id/bid { amount, memo }
// 정책: 최소 10000원, 하향 금지, 1회 수정만 가능

import React, { useState } from 'react';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import { useNavigate } from 'react-router-dom';

function formatKrw(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString() + '원';
}

function AuctionBuyerBidModal(props) {
  var auction = props.auction || {};
  var existingBid = props.existingBid;       // { amount, status, updateCount } 또는 null
  var onClose = props.onClose;
  var onSuccess = props.onSuccess;
  var navigate = useNavigate();

  var [manwon, setManwon] = useState('');       // 만원 단위 입력 (UX)
  var [memo, setMemo] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);

  var amount = manwon ? parseInt(manwon, 10) * 10000 : 0;
  var minAmount = existingBid ? (existingBid.amount + 10000) : 10000;
  var minManwon = Math.ceil(minAmount / 10000);

  var canSubmit = amount >= minAmount && !loading;

  var handleSubmit = function() {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    authBuyerFetch(API + '/buyer-app/auction/' + auction.id + '/bid', {
      method: 'POST',
      body: JSON.stringify({ amount: amount, memo: memo.trim() }),
    })
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json().then(function(d) { return { status: r.status, body: d }; });
      })
      .then(function(res) {
        setLoading(false);
        if (res.body && res.body.success) {
          if (onSuccess) onSuccess(res.body);
          if (onClose) onClose();
          return;
        }
        var code = (res.body && res.body.code) || '';
        if (code === 'BID_TOO_LOW') setError('입찰 금액은 1만원 이상이어야 합니다.');
        else if (code === 'BID_NOT_HIGHER') setError('기존 입찰가보다 높은 금액이어야 합니다.');
        else if (code === 'BID_UPDATE_LIMIT') setError('입찰 수정은 1회만 가능합니다.');
        else if (code === 'AUCTION_ENDED') setError('마감된 경매입니다.');
        else if (code === 'AUCTION_NOT_FOUND') setError('경매를 찾을 수 없습니다.');
        else setError((res.body && res.body.error) || '입찰 실패');
      })
      .catch(function(e) {
        if (e && e.message === 'unauthorized') return;
        setLoading(false);
        setError('네트워크 오류');
      });
  };

  return (
    <div onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        fontFamily: "'Pretendard',sans-serif",
      }}>
      <div onClick={function(e){e.stopPropagation();}}
        style={{
          background: 'white', borderRadius: 16,
          width: 'calc(100% - 40px)', maxWidth: 480,
          padding: 28,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
            {existingBid ? '입찰 수정' : '입찰하기'}
          </h2>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* 차량 정보 요약 */}
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
            {auction.car && auction.car.year ? auction.car.year + ' ' : ''}{(auction.car && auction.car.model) || auction.title || '경매 차량'}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>
            현재 최고가: {auction.highestBid > 0 ? formatKrw(auction.highestBid) : '입찰 없음'}
          </div>
        </div>

        {existingBid && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>현재 내 입찰: {formatKrw(existingBid.amount)}</div>
            <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
              ⚠ 입찰 수정은 1회만 가능합니다 (수정 {existingBid.updateCount || 0}/1)
            </div>
          </div>
        )}

        {/* 입찰 금액 (만원 단위) */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            입찰 금액 (만원 단위, 최소 {minManwon}만원)
          </label>
          <div style={{ position: 'relative' }}>
            <input type="number" value={manwon} onChange={function(e){setManwon(e.target.value);}}
              placeholder={minManwon + ''}
              min={minManwon}
              style={{
                width: '100%', height: 48, padding: '0 60px 0 16px',
                border: '1.5px solid #e2e8f0', borderRadius: 10,
                fontSize: 18, fontWeight: 700, outline: 'none',
                background: '#f8fafc',
                fontFamily: "'Pretendard',sans-serif",
                boxSizing: 'border-box',
              }}
            />
            <span style={{ position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 600, color: '#475569' }}>
              만원
            </span>
          </div>
          {amount > 0 && (
            <div style={{ fontSize: 13, color: '#1B3A5C', fontWeight: 700, marginTop: 8 }}>
              = {formatKrw(amount)}
            </div>
          )}
        </div>

        {/* 메모 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            메모 (선택)
          </label>
          <textarea value={memo} onChange={function(e){setMemo(e.target.value);}}
            maxLength={500}
            rows={2}
            placeholder="출품자에게 메시지 (선택)"
            style={{
              width: '100%', padding: '10px 14px',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, outline: 'none', resize: 'vertical',
              background: '#f8fafc',
              fontFamily: "'Pretendard',sans-serif",
              boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#991b1b', fontSize: 13 }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose}
            style={{
              flex: 1, height: 48,
              background: 'white', color: '#475569',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Pretendard',sans-serif",
            }}>
            취소
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit}
            style={{
              flex: 2, height: 48,
              background: canSubmit ? 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)' : '#cbd5e1',
              color: 'white', border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontFamily: "'Pretendard',sans-serif",
              boxShadow: canSubmit ? '0 4px 12px rgba(27,58,92,0.2)' : 'none',
            }}>
            {loading ? '입찰 중...' : (existingBid ? '입찰 수정' : '입찰하기')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuctionBuyerBidModal;
